import { useServiceError } from '@/hooks';
import { Modal, notification } from 'antd';
import { useIntl } from 'react-intl';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';
import { DEFAULT_ROWS, consultInvoiceDicionarySuccessMessage } from '..';
import {
  cancelInvoice,
  consultInvoice,
  cancelConsultInvoice,
  createInvoice,
  deleteInvoice,
  invoiceDocumentDownload,
  getInvoiceId,
  listInvoice,
  sendEmailInvoice,
  transmitInvoice,
  updateInvoice,
  updateInvoiceAdditional,
  resolveInvoice,
  invoiceRpsDocument,
  invoiceUploadDocument,
  invoiceTefPaymentAvailability,
  invoiceTefPaymentMethod,
  invoiceSpedList,
  cancelNationalNfs,
} from './Invoice.services';
import {
  InvoiceCreateRequest,
  InvoiceListParams,
  DataInvoiceUpdate,
  UpdateInvoiceAdditionalDTO,
  CancelInvoiceRequest,
  InvoiceResponseError,
  DownloadXmlInvoiceResponseMessage,
  ConsultInvoiceStatus,
  SendEmailRequest,
  InvoiceSpedListRequest,
} from './Invoice.types';
import { InvoiceType } from '../invoices/invoices.enums';
import { useRouteMatch } from 'react-router-dom';
import invoiceHandleError from './invoice.error.transmit';
import { AxiosError } from 'axios';
import arrayBufferParse from '@/utils/functions/arrayBufferParse';
import notificationError from '@/utils/functions/notificationError';
import { queryClient } from '@/Root';

const INVOICE_LIST_QUERY = 'invoices';
const INVOICE_DETAIL_QUERY = 'invoice';

export function useInvoices(params?: InvoiceListParams, enabled = true) {
  const query = useInfiniteQuery(
    [INVOICE_LIST_QUERY, params],
    ({ pageParam }) => {
      const queryParams: InvoiceListParams = {
        nextToken: pageParam,
        invoiceType: InvoiceType.NFE,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return listInvoice(queryParams);
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextToken,
      enabled,
    },
  );

  useServiceError(query.error);

  return query;
}

export function useGetInvoiceId(invoiceId: number) {
  return useQuery(
    [INVOICE_DETAIL_QUERY, invoiceId],
    () => getInvoiceId(invoiceId),
    {
      enabled: !!invoiceId,
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useGetInvoiceIdMutation() {
  const mutation = useMutation((invoiceId: number) => getInvoiceId(invoiceId));
  useServiceError(mutation.error);
  return mutation;
}

export function useCreateInvoice() {
  const intl = useIntl();
  const mutation = useMutation(
    (obj: InvoiceCreateRequest) => createInvoice(obj),
    {
      onSuccess() {
        notification.success({
          message: intl.formatMessage({
            id: 'invoice.notifications.create.success.title',
          }),
          description: intl.formatMessage({
            id: 'invoice.notifications.create.success.description',
          }),
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useDeleteInvoice() {
  const intl = useIntl();

  const queryClient = useQueryClient();

  const mutation = useMutation((id: number) => deleteInvoice(id), {
    async onSuccess() {
      notification.success({
        message: intl.formatMessage({
          id: 'invoice.remove.notification.success',
        }),
        description: intl.formatMessage({
          id: 'invoice.status.message.success',
        }),
      });

      await queryClient.invalidateQueries([INVOICE_LIST_QUERY]);
    },
    onError(error) {
      notificationError(error);
    },
  });

  return mutation;
}

export function useUpdateInvoice() {
  const intl = useIntl().formatMessage;
  const queryClient = useQueryClient();

  const mutation = useMutation((obj: DataInvoiceUpdate) => updateInvoice(obj), {
    async onSuccess({ id }) {
      notification.success({
        message: intl({
          id: 'invoice.create.notification.success',
        }),
        description: intl({
          id: 'invoice.create.notification.successDescription.edit',
        }),
      });

      await queryClient.invalidateQueries([INVOICE_DETAIL_QUERY, id]);
    },
  });
  useServiceError(mutation.error);
  return mutation;
}

//Botões

export function useTransmitInvoice() {
  const queryClient = useQueryClient();
  const mutation = useMutation((id: number) => transmitInvoice(id), {
    async onSuccess(data, id) {
      if (data.status === ConsultInvoiceStatus.rejeitado) {
        Modal.error({
          title: 'Rejeitado',
          content: data.message,
        });
      } else if (data.status === ConsultInvoiceStatus.processando) {
        notification.info({
          message: 'Situação',
          description: data.status,
        });
      } else if (
        data.status === ConsultInvoiceStatus.cancelado ||
        data.status === ConsultInvoiceStatus.denegado
      ) {
        notification.error({
          message: 'Situação',
          description: data.status,
        });
      } else {
        notification.success({
          message: 'Situação',
          description: data.status,
        });
      }

      await queryClient.invalidateQueries([INVOICE_DETAIL_QUERY, id]);
    },

    onError(error) {
      invoiceHandleError(error);
    },
  });

  return mutation;
}

export function useCancelInvoice() {
  const queryClient = useQueryClient();
  const mutation = useMutation(
    (obj: CancelInvoiceRequest) => cancelInvoice(obj),
    {
      async onSuccess(_, data) {
        notification.info({
          message: 'Situação',
          description: 'Nota Fiscal em cancelamento',
        });

        await queryClient.invalidateQueries([
          INVOICE_DETAIL_QUERY,
          data.invoiceId,
        ]);
      },
      onError(error) {
        const err = error as AxiosError;

        if (!!err.response?.data.error.message && err.response.status === 400) {
          notification.error({
            message: 'Impossibilidade de envio',
            description: err.response?.data.error.message,
          });
        } else {
          notificationError(error);
        }
      },
    },
  );

  return mutation;
}

export function useResolveInvoice() {
  const queryClient = useQueryClient();
  return useMutation((invoiceId: number) => resolveInvoice(invoiceId), {
    async onSuccess(_, invoiceId) {
      notification.success({
        message: 'Sucesso',
        description: 'Nota Fiscal cancelada',
      });

      await queryClient.invalidateQueries([INVOICE_DETAIL_QUERY, invoiceId]);
    },
    onError(error) {
      const err = error as AxiosError;

      if (!!err.response?.data.error.message && err.response.status === 400) {
        notification.error({
          message: 'Aguarde um momento',
          description: err.response?.data.error.message,
        });
      } else {
        notificationError(error);
      }
    },
  });
}

export function useCancelNationalNfs() {
  const queryClient = useQueryClient();
  return useMutation((invoiceId: number) => cancelNationalNfs(invoiceId), {
    async onSuccess(_, invoiceId) {
      notification.success({
        message: 'Alteração de status realizada',
        description:
          'A situação da Nota Fiscal foi alterada para cancelada internamente.',
      });

      await queryClient.invalidateQueries([INVOICE_DETAIL_QUERY, invoiceId]);
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useSendEmailInvoice() {
  const intl = useIntl();
  const mutation = useMutation(
    (obj: SendEmailRequest) => sendEmailInvoice(obj),
    {
      onSuccess() {
        notification.success({
          message: intl.formatMessage({
            id: 'invoice.notifications.create.success.title',
          }),
          description: 'Email enviado com sucesso!',
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useConsultInvoiceId() {
  const { invoiceId } = useRouteMatch<{ invoiceId: string }>().params;
  const queryClient = useQueryClient();
  const mutation = useMutation((id: number) => consultInvoice(id), {
    async onSuccess(data) {
      const notificationResult = {
        message: 'Situação',
        description:
          consultInvoiceDicionarySuccessMessage[data.message] || data.status,
        duration: 20,
      };

      if (data.status === ConsultInvoiceStatus.rejeitado) {
        notification.error(notificationResult);
      } else if (
        data.status === ConsultInvoiceStatus.cancelado ||
        data.status === ConsultInvoiceStatus.denegado
      ) {
        notification.error({ message: 'Situação', description: data.status });
      } else if (data.status === ConsultInvoiceStatus.processando) {
        notification.info(notificationResult);
      } else {
        notification.success(notificationResult);
      }

      await queryClient.invalidateQueries([
        INVOICE_DETAIL_QUERY,
        Number(invoiceId),
      ]);
    },

    onError(error) {
      const err = error as AxiosError<InvoiceResponseError>;

      if (err.response?.data.error.message) {
        notification.error({ message: err.response?.data.error.message });
      } else {
        useServiceError(mutation.error);
      }
    },
  });

  return mutation;
}

export function useCancelConsultInvoiceId() {
  const { invoiceId } = useRouteMatch<{ invoiceId: string }>().params;
  const queryClient = useQueryClient();

  const mutation = useMutation(cancelConsultInvoice, {
    async onSuccess(data) {
      const notificationResult = {
        message: 'Situação',
        description: data.message,
        duration: 20,
      };

      if (data.data.inProcess) {
        return notification.info(notificationResult);
      }

      if (data.data.status === ConsultInvoiceStatus.rejeitado) {
        if (!!data.data?.response) {
          notification.error({
            ...notificationResult,
            description: data.data.response,
          });
        } else {
          notification.error(notificationResult);
        }
      } else {
        notification.success(notificationResult);
      }

      await queryClient.invalidateQueries([
        INVOICE_DETAIL_QUERY,
        Number(invoiceId),
      ]);
    },

    onError(error) {
      const err = error as AxiosError<InvoiceResponseError>;

      if (err.response?.data.error.message) {
        notification.info({
          message: 'Situação',
          description: err.response?.data.error.message,
        });
      } else {
        notificationError(error);
      }
    },
  });

  return mutation;
}

export function useInvoiceDocumentDownload() {
  const intl = useIntl();

  const mutation = useMutation(invoiceDocumentDownload, {
    onSuccess(data, vars) {
      if (data) {
        const response =
          arrayBufferParse<DownloadXmlInvoiceResponseMessage>(data);

        if (response?.message) {
          return notification.warn({
            message: 'Processando documento',
            description: response.message,
          });
        }
      }

      notification.success({
        message: intl.formatMessage({
          id: 'invoice.notifications.create.success.title',
        }),
        description: `O "${
          vars.docType === 0 ? 'XML' : 'PDF'
        }" da nota fiscal executado com sucesso!`,
      });
    },

    onError(error) {
      const err = error as AxiosError<ArrayBufferLike>;

      if (err.response?.data) {
        const response = arrayBufferParse<InvoiceResponseError>(
          err.response?.data,
        );

        return notification.error({
          message: 'Erro na requisição',
          description: response?.error.message,
        });
      }
    },
  });
  return mutation;
}

export function useInvoiceRpsDocument() {
  const mutation = useMutation(invoiceRpsDocument, {
    onError(error) {
      const err = error as AxiosError<ArrayBufferLike>;

      if (err.response?.data) {
        const response = arrayBufferParse<InvoiceResponseError>(
          err.response?.data,
        );

        return notification.error({
          message: 'Erro na requisição',
          description: response?.error.message,
        });
      }
    },
  });
  return mutation;
}

export function useInvoiceUploadDocument() {
  const mutation = useMutation(invoiceUploadDocument, {
    onError(error) {
      const err = error as AxiosError<ArrayBufferLike>;

      if (err.response?.data) {
        const response = arrayBufferParse<InvoiceResponseError>(
          err.response?.data,
        );

        return notification.error({
          message: 'Erro na requisição',
          description: response?.error.message,
        });
      }
    },
  });
  return mutation;
}

// Dados Adicionais

export function useUpdateInvoiceAdditional() {
  const intl = useIntl().formatMessage;
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (obj: UpdateInvoiceAdditionalDTO) => updateInvoiceAdditional(obj),
    {
      async onSuccess(_, data) {
        notification.success({
          message: intl({
            id: 'invoice.create.notification.additionalData.edit',
          }),
        });

        await queryClient.invalidateQueries([
          INVOICE_DETAIL_QUERY,
          data.invoiceId,
        ]);
      },
    },
  );
  useServiceError(mutation.error);
  return mutation;
}

export function useInvoiceTefPaymentMethod() {
  return useMutation({
    mutationFn: invoiceTefPaymentMethod,
    async onSuccess(_, invoiceId) {
      notification.success({
        message: 'Pagamentos vinculados com sucesso',
      });

      await queryClient.invalidateQueries(['paymentMethodInvoice', invoiceId]);
      await queryClient.invalidateQueries([
        'TEF-PAYMENT-AVAILABILITY',
        invoiceId,
      ]);
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useInvoiceTefPaymentAvailability(invoiceId: number) {
  return useQuery({
    queryKey: ['TEF-PAYMENT-AVAILABILITY', invoiceId],
    queryFn: () => invoiceTefPaymentAvailability(invoiceId),
    enabled: !!invoiceId,
  });
}

export function useInvoiceSpedList(
  params: InvoiceSpedListRequest,
  enabled = true,
) {
  return useQuery({
    queryKey: ['INVOICE-SPED-LIST', params],
    queryFn: () => invoiceSpedList(params),
    enabled: !!params.companyId && !!params.month && !!params.year && enabled,
  });
}
